import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { resetPassword, signIn, signUp } from "../firebase"; // zakładam, że masz to
import useCtx from "../hooks/useCts";
import { blurActiveElement } from "../utils/helpers";

const TeacherLogin: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolToken, setSchoolToken] = useState("");
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const ctx = useCtx();

  const resetFields = () => {
    blurActiveElement();
    setEmail("");
    setPassword("");
    setIsRegistering(false);
    setIsAccepted(false);
  };

  const handleError = (e: any) => {
    switch (e) {
      case "auth/weak-password":
        setError("Hasło musi mieć co najmniej 8 znaków.");
        break;
      case "auth/email-already-in-use":
        setError("Ten email jest już zarejestrowany.");
        break;
      case "auth/invalid-credential":
      case "auth/user-not-found":
        setError("Nieprawidłowy email lub hasło.");
        break;
      default:
        setError(
          "Coś poszło nie tak. Sprawdź czy hasło ma conajmniej 8 znaków, 1 cyfrę i 1 dużą literę.",
        );
    }
  };

  const handleAuthAction = async () => {
    blurActiveElement();
    setError("");
    const client = ctx.clients.find((c) => c.token === schoolToken);

    const teacherExists = client?.teachers?.some(
      (t: any) => t.email.toLowerCase() === email.toLowerCase(),
    );

    if (isRegistering) {
      if (!isAccepted) {
        setError("Musisz zaakceptować regulamin.");
        return;
      }

      if (!client) {
        setError("Nieprawidłowy token dostepu.");
        return;
      }

      if (!teacherExists) {
        setError(
          "Twoja szkoła nie dodała Cię w panelu szkoły do listy lektorów.",
        );
        return;
      }

      try {
        await signUp(email, password);
      } catch (e: any) {
        handleError(e);
      }
    } else {
      if (!teacherExists) {
        setError(
          "Twoja szkoła nie dodała Cię w panelu szkoły do listy lektorów.",
        );
        resetFields();
        return;
      }

      try {
        await signIn(email, password);
      } catch (e: any) {
        handleError(e);
      }
    }
  };

  const handleForgotPassword = () => {
    blurActiveElement();

    if (email) {
      resetPassword(email)
        .then(() => {
          alert("Link do zresetowania hasła został wysłany na podany email.");
        })
        .catch((e) => {
          console.error("Error during password reset:", e);
          setError("Wystąpił błąd podczas resetowania hasła.");
        })
        .finally(() => {
          setError("");
        });
    } else {
      setError("Podaj email, aby zresetować hasło.");
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="my-6 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
      >
        <ArrowLeft size={16} strokeWidth={2.2} aria-hidden="true" />
        Wróć do wyboru konta
      </button>

      <div>
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
            Panel logowania
          </p>
          <h2 className="mt-3 bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text text-xl font-semibold tracking-[-0.03em] text-transparent sm:text-2xl">
            {isRegistering
              ? "Rejestracja jako lektor"
              : "Logowanie jako lektor"}
          </h2>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="teacher-email" className="sr-only">
              Email
            </label>
            <input
              id="teacher-email"
              name="email"
              type="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              inputMode="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-base text-slate-800 shadow-[0_10px_24px_rgba(148,163,184,0.12)] outline-none transition placeholder:text-slate-400 focus:border-orange-200 focus:ring-4 focus:ring-orange-200/50 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="teacher-password" className="sr-only">
              Hasło
            </label>
            <input
              id="teacher-password"
              name="password"
              type="password"
              autoComplete={isRegistering ? "new-password" : "current-password"}
              placeholder={
                isRegistering
                  ? "Ustaw nowe hasło (Min. 1 duża litera, 1 cyfra, 8 znaków)"
                  : "Hasło"
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-base text-slate-800 shadow-[0_10px_24px_rgba(148,163,184,0.12)] outline-none transition placeholder:text-slate-400 focus:border-orange-200 focus:ring-4 focus:ring-orange-200/50 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="teacher-school-token" className="sr-only">
              Token dostępu
            </label>
            <input
              id="teacher-school-token"
              name="schoolToken"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="Token dostępu"
              value={schoolToken}
              onChange={(e) => setSchoolToken(e.target.value)}
              className="w-full rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-base text-slate-800 shadow-[0_10px_24px_rgba(148,163,184,0.12)] outline-none transition placeholder:text-slate-400 focus:border-orange-200 focus:ring-4 focus:ring-orange-200/50 sm:text-sm"
            />
          </div>

          {isRegistering && (
            <div className="rounded-2xl border border-white/75 bg-white/68 px-4 py-4 text-sm text-slate-700 shadow-[0_10px_24px_rgba(148,163,184,0.1)]">
              <label className="flex flex-wrap items-start">
                <input
                  type="checkbox"
                  checked={isAccepted}
                  onChange={() => setIsAccepted(!isAccepted)}
                  className="mr-3 mt-1 flex-shrink-0"
                />
                <span className="flex-1 flex flex-wrap items-center">
                  Akceptuję
                  <a
                    href="/regulamin"
                    className="mx-1 whitespace-nowrap text-sky-700 underline"
                    target="_blank"
                  >
                    regulamin
                  </a>
                  oraz
                  <a
                    href="/polityka-prywatnosci"
                    className="mx-1 whitespace-nowrap text-sky-700 underline"
                    target="_blank"
                  >
                    politykę prywatności.
                  </a>
                </span>
              </label>
              {!isAccepted && (
                <p className="mt-2 text-xs text-rose-500">
                  * Aby się zarejestrować, zaznacz zgodę.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-2xl border border-rose-200/90 bg-rose-50/90 px-4 py-3 text-sm text-rose-700 shadow-[0_14px_30px_rgba(244,63,94,0.12)]">
              {error}
            </div>
          )}

          {isRegistering && (
            <p className="text-xs leading-6 text-slate-600">
              Administratorem danych jest{" "}
              <strong>Directpress sp. z o.o.</strong>. Dane będą przetwarzane
              zgodnie z{" "}
              <a
                href="/polityka-prywatnosci"
                className="text-sky-700 underline"
                target="_blank"
              >
                polityką prywatności
              </a>
              .
            </p>
          )}

          <button
            onClick={handleAuthAction}
            className="w-full rounded-full bg-[linear-gradient(135deg,#38bdf8,#0ea5e9)] shadow-[0_16px_36px_rgba(14,165,233,0.28)] transition hover:brightness-105 px-6 py-3 text-sm font-semibold text-white "
          >
            {isRegistering
              ? "Zarejestruj się jako lektor"
              : "Zaloguj się jako lektor"}
          </button>
          <p className="text-center text-sm text-slate-600">
            {isRegistering ? "Masz już konto?" : "Nie masz jeszcze konta?"}{" "}
            <button
              onClick={() => {
                setIsRegistering(!isRegistering);
                setError("");
              }}
              className="text-sky-700 underline"
            >
              {isRegistering ? "Zaloguj się" : "Zarejestruj się"}
            </button>
          </p>
          <p
            onClick={handleForgotPassword}
            className="text-center text-sm text-sky-700 underline cursor-pointer"
          >
            Zapomniałeś hasła?
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeacherLogin;
